import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLanguageFromPath, removeLanguagePrefix, type SupportedLanguage } from '@/i18n/config';

export const LanguageRouter = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  useEffect(() => {
    const pathLang = getLanguageFromPath(location.pathname);
    
    // If the URL has a language prefix, set i18n to that language
    if (pathLang !== 'pt' && i18n.language !== pathLang) {
      i18n.changeLanguage(pathLang);
    }
    
    // If we're on a Portuguese path but i18n is set to another language,
    // and the path doesn't have a language prefix, keep Portuguese
    if (pathLang === 'pt' && !location.pathname.match(/^\/(en|es|pl|fr)(\/|$)/)) {
      if (i18n.language !== 'pt') {
        // Only change if explicitly navigating to Portuguese content
        const storedLang = localStorage.getItem('i18nextLng');
        if (!storedLang || storedLang === 'pt') {
          i18n.changeLanguage('pt');
        }
      }
    }
  }, [location.pathname, i18n]);

  return <>{children}</>;
};

// Hook to get the correct path with language prefix
export const useLocalizedPath = () => {
  const { i18n } = useTranslation();
  
  const getPath = (basePath: string): string => {
    const lang = i18n.language as SupportedLanguage;
    if (lang === 'pt') return basePath;
    return `/${lang}${basePath}`;
  };
  
  return { getPath };
};

